import React, { useEffect, useState } from 'react';
import { Button, InputNumber, Modal, Select, Switch, message } from 'antd';
import {
  getChatShare,
  getGuestSharePolicy,
  regenerateChatShare,
  saveChatShare,
  searchShareUsers,
  updateChatShareSnapshot,
} from '../../services/api/chatShares';
import './ChatShareDialog.css';

const VISIBILITY = [
  {
    value: 'invited',
    title: 'Only people invited',
    help: 'A platform account you add below.',
  },
  {
    value: 'authenticated',
    title: 'Any signed-in user',
    help: 'Anyone with an account and this link.',
  },
  {
    value: 'public',
    title: 'Anyone with the link',
    help: 'No account required.',
  },
];

export default function ChatShareDialog({ open, onClose, keycloak, assistantId, sessionId, canShare }) {
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState({ enabled: false, per_share_max: null });
  const [mode, setMode] = useState('invited');
  const [keepUpdating, setKeepUpdating] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [chatOnly, setChatOnly] = useState(false);
  const [allowEmbed, setAllowEmbed] = useState(false);
  const [cap, setCap] = useState(null);
  const [invites, setInvites] = useState([]);
  const [url, setUrl] = useState('');
  const [opens, setOpens] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    if (!open || !canShare) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [guestPolicy, existing] = await Promise.all([
          getGuestSharePolicy(keycloak),
          getChatShare(keycloak, assistantId, sessionId),
        ]);
        if (cancelled) return;
        setPolicy(guestPolicy);
        const share = existing.share;
        if (share) {
          setMode(share.mode);
          setKeepUpdating(share.keep_updating);
          setEnabled(share.enabled);
          setChatOnly(Boolean(share.chat_only));
          setAllowEmbed(Boolean(share.allow_embed));
          setCap(share.continue_cap);
          setInvites(share.invite_user_ids || []);
          setUrl(share.url || '');
          setOpens(share.opens || []);
        } else {
          setMode('invited');
          setKeepUpdating(false);
          setEnabled(true);
          setChatOnly(false);
          setAllowEmbed(false);
          setCap(null);
          setInvites([]);
          setUrl('');
          setOpens([]);
        }
      } catch (err) {
        if (!cancelled) message.error(err.message || 'Could not load share');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, canShare, keycloak, assistantId, sessionId]);

  const onMode = (next) => {
    if (next === 'public' && !policy.enabled) return;
    setMode(next);
    setEnabled(true);
    if (next === 'public' && policy.per_share_max) setCap(policy.per_share_max);
  };

  const save = async () => {
    setLoading(true);
    try {
      const result = await saveChatShare(keycloak, assistantId, {
        session_id: sessionId,
        mode,
        keep_updating: keepUpdating,
        continue_cap: cap,
        enabled,
        chat_only: chatOnly,
        allow_embed: allowEmbed,
        invite_user_ids: invites,
      });
      const share = result.share;
      setUrl(share.url || '');
      setEnabled(share.enabled);
      setOpens(share.opens || []);
      message.success('Share updated');
    } catch (err) {
      message.error(err.message || 'Could not save share');
    } finally {
      setLoading(false);
    }
  };

  const updateSnapshot = async () => {
    try {
      const result = await updateChatShareSnapshot(keycloak, assistantId, sessionId);
      setUrl(result.share?.url || url);
      message.success('Shared transcript updated');
    } catch (err) {
      message.error(err.message || 'Could not update the transcript');
    }
  };

  const regenerate = async () => {
    try {
      const result = await regenerateChatShare(keycloak, assistantId, sessionId);
      setUrl(result.share?.url || '');
      message.success('New link created');
    } catch (err) {
      message.error(err.message || 'Could not regenerate');
    }
  };

  return (
    <Modal
      className="share-chat-modal"
      title="Share chat"
      open={open}
      onCancel={onClose}
      width={440}
      destroyOnClose
      footer={(
        <Button type="primary" loading={loading} onClick={save}>
          Save
        </Button>
      )}
    >
      <div className="share-dialog-label">Who can open this</div>
      <div className="share-dialog-choices">
        {VISIBILITY.map((option) => {
          const disabled = option.value === 'public' && !policy.enabled;
          return (
            <button
              key={option.value}
              type="button"
              className={`share-dialog-choice${mode === option.value ? ' is-selected' : ''}`}
              disabled={disabled}
              onClick={() => onMode(option.value)}
            >
              <span className="share-dialog-choice-title">{option.title}</span>
              <span className="share-dialog-choice-help">
                {disabled ? 'Turned off for this platform.' : option.help}
              </span>
            </button>
          );
        })}
      </div>
      {mode === 'invited' ? (
        <Select
          className="share-dialog-people"
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Search platform accounts"
          value={invites}
          onChange={setInvites}
          filterOption={false}
          onSearch={async (q) => {
            if ((q || '').trim().length < 2) return;
            try {
              const users = await searchShareUsers(keycloak, assistantId, q);
              setUserOptions(users.map((user) => ({
                value: user.id,
                label: user.label,
              })));
            } catch {
              setUserOptions([]);
            }
          }}
          options={userOptions}
        />
      ) : null}

      <div className="share-dialog-settings">
        <div className="share-dialog-row">
          <div className="share-dialog-row-text">
            <div className="share-dialog-row-title">Keep updating</div>
            <span className="share-dialog-help">New messages in this chat appear on the link.</span>
          </div>
          <Switch size="small" checked={keepUpdating} onChange={setKeepUpdating} />
        </div>
        {!keepUpdating ? (
          <div className="share-dialog-inline">
            <button type="button" className="share-dialog-text" onClick={updateSnapshot}>
              Update shared transcript
            </button>
          </div>
        ) : null}
        <div className="share-dialog-row">
          <div className="share-dialog-row-text">
            <div className="share-dialog-row-title">Chat only</div>
            <span className="share-dialog-help">Hide the sidebar and header. People only see the conversation.</span>
          </div>
          <Switch size="small" checked={chatOnly} onChange={setChatOnly} />
        </div>
        <div className="share-dialog-row">
          <div className="share-dialog-row-text">
            <div className="share-dialog-row-title">Allow embed</div>
            <span className="share-dialog-help">Other sites can place this link in a frame.</span>
          </div>
          <Switch size="small" checked={allowEmbed} onChange={setAllowEmbed} />
        </div>
        <div className="share-dialog-row">
          <div className="share-dialog-row-text">
            <div className="share-dialog-row-title">Continue cap</div>
            <span className="share-dialog-help">
              {mode === 'public' && policy.per_share_max
                ? `Messages someone can send. Public links stop at ${policy.per_share_max}.`
                : 'Messages someone can send on this link.'}
            </span>
          </div>
          <InputNumber
            size="small"
            min={1}
            max={mode === 'public' ? policy.per_share_max || undefined : undefined}
            value={cap}
            onChange={setCap}
            style={{ width: 88 }}
          />
        </div>
        <div className="share-dialog-row">
          <div className="share-dialog-row-text">
            <div className="share-dialog-row-title">Link</div>
            <span className="share-dialog-help">
              {enabled ? 'People you allowed can open it.' : 'Opening the link shows not found.'}
            </span>
          </div>
          <Switch size="small" checked={enabled} onChange={setEnabled} />
        </div>
      </div>

      {url ? (
        <div className="share-dialog-link">
          <input value={url} readOnly aria-label="Share link" />
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(url);
              message.success('Link copied');
            }}
          >
            Copy
          </button>
        </div>
      ) : null}
      {url ? (
        <div className="share-dialog-regen">
          <button type="button" className="share-dialog-text" onClick={regenerate}>
            Regenerate link
          </button>
          <span className="share-dialog-help">The current link stops working.</span>
        </div>
      ) : null}
      {opens.length > 0 ? (
        <div className="share-dialog-note">
          Opened {opens.length} {opens.length === 1 ? 'time' : 'times'}
        </div>
      ) : null}
    </Modal>
  );
}
